<script lang="ts">
	import { onDestroy } from 'svelte';
	import { createWsStore } from '$lib/ws.svelte';
	import { apiGet, apiPost, ApiError } from '$lib/api';
	import { serverIcon } from '$lib/serverIcons';
	import { Play, Square, RotateCw, Save, Sword, PawPrint, Server } from '@lucide/svelte';

	type ServiceInfo = { key: string; label: string; status: string };
	type Overview = { services: Record<string, ServiceInfo> };
	type Instance = {
		slug: string;
		label: string;
		icon: string;
		install_state: string;
		status?: string;
	};

	const ws = createWsStore<Overview>('/ws/overview');
	const gameIcons: Record<string, typeof Sword> = { valheim: Sword, palworld: PawPrint };

	// Le catalogue générique (serveurs créés/supprimés dynamiquement) n'est pas
	// diffusé sur le flux WS /ws/overview (qui ne connaît que les deux jeux
	// historiques) -- ce panneau sonde /api/servers séparément pour rester
	// vraiment dynamique selon le nombre réel de serveurs, pas figé à 2.
	let catalogInstances = $state<Instance[]>([]);
	async function loadCatalog() {
		try {
			const res = await apiGet<{ servers: Record<string, Instance> }>('/api/servers');
			catalogInstances = Object.values(res.servers).filter((s) => s.install_state === 'ready');
		} catch {
			// Panneau secondaire -- une erreur ici ne doit pas empêcher le reste
			// du tableau de bord de fonctionner.
		}
	}
	let catalogPollTimer: ReturnType<typeof setInterval> | undefined;
	$effect(() => {
		loadCatalog();
		catalogPollTimer = setInterval(loadCatalog, 20000);
		return () => clearInterval(catalogPollTimer);
	});

	type Row = {
		rowKind: 'flagship' | 'catalog';
		key: string;
		label: string;
		status: string;
		Icon: typeof Sword;
	};
	const rows = $derived<Row[]>([
		...(ws.data
			? Object.values(ws.data.services).map((s) => ({
					rowKind: 'flagship' as const,
					key: s.key,
					label: s.label,
					status: s.status,
					Icon: gameIcons[s.key] ?? Server
				}))
			: []),
		...catalogInstances.map((s) => ({
			rowKind: 'catalog' as const,
			key: s.slug,
			label: s.label,
			status: s.status ?? 'unknown',
			Icon: serverIcon(s.icon)
		}))
	]);

	let busyKey = $state<string | null>(null);
	let feedback = $state<{ row: string; kind: 'good' | 'critical'; text: string } | null>(null);

	// Stop/restart disconnect every connected player -- this compact icon-only
	// row has no room for ConfirmButton's inline Confirmer/Annuler pair without
	// breaking the 3-column layout, so instead: first click "arms" the button
	// (visually distinct, reverts after 3s if untouched), second click within
	// that window actually runs it. Start/save stay single-click (non-disruptive).
	let armedKey = $state<string | null>(null);
	let armTimer: ReturnType<typeof setTimeout> | undefined;
	onDestroy(() => clearTimeout(armTimer));

	function controlUrl(rowKind: 'flagship' | 'catalog', key: string, action: string) {
		return rowKind === 'flagship'
			? `/api/${key}/service/${action}`
			: `/api/servers/${key}/service/${action}`;
	}

	function requestControl(
		rowKind: 'flagship' | 'catalog',
		key: string,
		action: 'start' | 'stop' | 'restart'
	) {
		if (action === 'start') {
			control(rowKind, key, action);
			return;
		}
		const armKey = `${rowKind}:${key}:${action}`;
		if (armedKey === armKey) {
			clearTimeout(armTimer);
			armedKey = null;
			control(rowKind, key, action);
			return;
		}
		armedKey = armKey;
		clearTimeout(armTimer);
		armTimer = setTimeout(() => (armedKey = null), 3000);
	}

	async function control(
		rowKind: 'flagship' | 'catalog',
		key: string,
		action: 'start' | 'stop' | 'restart'
	) {
		const row = `${rowKind}:${key}`;
		busyKey = `${row}:${action}`;
		try {
			await apiPost(controlUrl(rowKind, key, action));
			flash(
				row,
				'good',
				action === 'start' ? 'Démarrage…' : action === 'stop' ? 'Arrêt…' : 'Redémarrage…'
			);
			if (rowKind === 'catalog') await loadCatalog();
		} catch (e) {
			flash(row, 'critical', e instanceof ApiError ? e.message : 'Échec.');
		} finally {
			busyKey = null;
		}
	}

	async function save(game: string) {
		const row = `flagship:${game}`;
		busyKey = `${row}:save`;
		try {
			await apiPost(`/api/${game}/save`);
			flash(row, 'good', 'Sauvegardé.');
		} catch (e) {
			flash(row, 'critical', e instanceof ApiError ? e.message : 'Échec.');
		} finally {
			busyKey = null;
		}
	}

	function flash(row: string, kind: 'good' | 'critical', text: string) {
		feedback = { row, kind, text };
		setTimeout(() => (feedback = null), 2500);
	}
</script>

<div class="quick">
	<span class="heading">Contrôles rapides</span>
	{#if !ws.data}
		<div class="skel skel-row" aria-hidden="true"></div>
		<div class="skel skel-row" aria-hidden="true"></div>
	{:else if rows.length === 0}
		<p class="empty-hint">Aucun serveur pour l'instant.</p>
	{:else}
		{#each rows as row (`${row.rowKind}:${row.key}`)}
			{@const isActive = row.status === 'active'}
			{@const rowId = `${row.rowKind}:${row.key}`}
			<div class="row" data-game={row.key}>
				<div class="label">
					<row.Icon size={13} />
					<span>{row.label}</span>
					<span class="dot" data-on={isActive}></span>
				</div>
				<div class="actions">
					{#if isActive}
						{#if row.rowKind === 'flagship'}
							<button
								aria-label="Sauvegarder {row.label}"
								title="Sauvegarder"
								disabled={busyKey === `${rowId}:save`}
								onclick={() => save(row.key)}><Save size={13} /></button
							>
						{/if}
						<button
							class="armable"
							class:armed={armedKey === `${rowId}:restart`}
							aria-label={armedKey === `${rowId}:restart`
								? `Confirmer le redémarrage de ${row.label}`
								: `Redémarrer ${row.label}`}
							title={armedKey === `${rowId}:restart`
								? 'Cliquer à nouveau pour confirmer'
								: 'Redémarrer'}
							disabled={busyKey === `${rowId}:restart`}
							onclick={() => requestControl(row.rowKind, row.key, 'restart')}
							><RotateCw size={13} /></button
						>
						<button
							class="danger armable"
							class:armed={armedKey === `${rowId}:stop`}
							aria-label={armedKey === `${rowId}:stop`
								? `Confirmer l'arrêt de ${row.label}`
								: `Arrêter ${row.label}`}
							title={armedKey === `${rowId}:stop` ? 'Cliquer à nouveau pour confirmer' : 'Arrêter'}
							disabled={busyKey === `${rowId}:stop`}
							onclick={() => requestControl(row.rowKind, row.key, 'stop')}
							><Square size={13} /></button
						>
					{:else}
						<button
							class="primary"
							aria-label="Démarrer {row.label}"
							title="Démarrer"
							disabled={busyKey === `${rowId}:start`}
							onclick={() => control(row.rowKind, row.key, 'start')}><Play size={13} /></button
						>
					{/if}
				</div>
				{#if feedback?.row === rowId}
					<span class="feedback" data-kind={feedback.kind}>{feedback.text}</span>
				{/if}
			</div>
		{/each}
	{/if}
</div>

<style>
	.quick {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		padding: var(--space-3);
		border-radius: var(--radius-md);
		background: var(--surface-2);
		border: 1px solid var(--border);
	}
	.heading {
		font-size: 10px;
		font-weight: 800;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--text-muted);
	}
	.empty-hint {
		font-size: var(--text-xs);
		color: var(--text-muted);
		margin: 0;
	}
	.skel-row {
		height: 46px;
		margin: var(--space-2) 0;
	}
	.row {
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
		padding: var(--space-1) 0;
		position: relative;
	}
	.row + .row {
		border-top: 1px solid var(--border);
		padding-top: var(--space-2);
	}
	.label {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-size: var(--text-xs);
		font-weight: 700;
		color: var(--text-secondary);
	}
	.row[data-game] .label :global(svg) {
		color: var(--accent);
	}
	.dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: var(--text-muted);
		margin-left: auto;
	}
	.dot[data-on='true'] {
		background: var(--good);
		box-shadow: 0 0 0 2px var(--surface-2);
		animation: pulse 2s var(--ease-in-out) infinite;
	}
	@keyframes pulse {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.4;
		}
	}
	.actions {
		display: flex;
		gap: 4px;
	}
	.actions button {
		flex: 1;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		height: 26px;
		border-radius: var(--radius-sm);
		border: 1px solid var(--border);
		background: var(--surface-1);
		color: var(--text-secondary);
		cursor: pointer;
		transition:
			border-color var(--dur-fast) var(--ease-out),
			color var(--dur-fast) var(--ease-out),
			background-color var(--dur-fast) var(--ease-out);
	}
	.actions button:hover:not(:disabled) {
		border-color: var(--accent);
		color: var(--accent);
	}
	.actions button:disabled {
		opacity: 0.5;
		cursor: default;
	}
	.actions button.primary {
		background: var(--accent);
		border-color: transparent;
		color: var(--accent-contrast);
	}
	.actions button.danger:hover:not(:disabled) {
		border-color: var(--critical);
		color: var(--critical);
	}
	.actions button.armable.armed {
		border-color: var(--critical);
		background: var(--critical-soft);
		color: var(--critical);
		animation: arm-pulse 1s var(--ease-in-out) infinite;
	}
	@keyframes arm-pulse {
		0%,
		100% {
			box-shadow: 0 0 0 0 var(--critical-soft);
		}
		50% {
			box-shadow: 0 0 0 3px var(--critical-soft);
		}
	}
	.feedback {
		position: absolute;
		top: -6px;
		right: 0;
		font-size: 10px;
		font-weight: 700;
		padding: 1px 6px;
		border-radius: var(--radius-full);
		animation: rise var(--dur-base) var(--ease-out);
	}
	.feedback[data-kind='good'] {
		background: var(--good-soft);
		color: var(--good-text);
	}
	.feedback[data-kind='critical'] {
		background: var(--critical-soft);
		color: var(--critical);
	}
	@keyframes rise {
		from {
			opacity: 0;
			transform: translateY(2px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
</style>
